import { MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';

/**
 * Opens a dialog and returns its `MatDialogRef`. Bound at the app composition root to
 * `(config) => matDialog.open(SomeDialogComponent, config)` so dialog-opening effects can live in
 * `platform/state` without importing the `shared/ui/core` dialog components (DEV-26105 T6, Option B —
 * the same token-inversion mechanism T5 established for `NOTIFY_CONTROL`/`USER_SERVICE`).
 *
 * Call sites keep their existing ergonomics: `this.openX({ width: '720px', data }).afterClosed()`.
 */
export type DialogOpener<In = unknown, Out = unknown> = (config?: MatDialogConfig<In>) => MatDialogRef<unknown, Out>;

/**
 * A `MatDialogRef`-shaped no-op so a token left unbound (unit tests, storybook) never null-injects.
 * Its `afterClosed()` emits `undefined`, matching what the real `MatDialog` emits when a dialog is
 * dismissed without a result (backdrop/ESC). In practice every dialog token is bound at the app
 * root and specs inject their own openers, so this default is a DI safety net, not a live code path.
 *
 * `getState()` returns `CLOSED` because some effects keep the returned ref and re-check its state
 * before reopening (e.g. `UsersStateEffects.openAddTeammatesDialog$` guards on
 * `ref.getState() !== MatDialogState.OPEN`). Without it, a second open on an unbound token would call
 * `undefined()` and throw; `CLOSED` lets the reopen proceed, matching the no-op's "nothing is shown".
 */
export function noopDialogRef<Out>(): MatDialogRef<unknown, Out> {
  return {
    afterClosed: (): Observable<Out | undefined> => of(undefined),
    close: (): void => undefined,
    getState: (): MatDialogState => MatDialogState.CLOSED,
  } as unknown as MatDialogRef<unknown, Out>;
}
